"use client";

/**
 * SelectedGuild store — kaynak-referans `navigation/state/SelectedGuild.ts` paritesi
 * (mobx `makeAutoObservable` singleton + `makePersistent`).
 *
 * kaynak-referansın `Navigation.guildId` üzerindeki mobx `reaction`'ı TEBHOS'ta
 * `syncFromRoute` aksiyonuna indirgenir; onu route'tan besleyen taraf
 * `hooks/useRouteSelectionSync`tir (TEBHOS'ta Navigation singleton'ı yok,
 * rota bilgisi react-router hook'larından gelir).
 *
 * Kalıcı alan (kaynak-referans allowlist paritesi): yalnızca `lastSelectedGuildId`.
 *
 * NOT: TEBHOS router'ında `@favorites` yoktur → DM ayracı `"@me"` normalize
 * edilerek "seçili sunucu yok" (null) sayılır (kaynak-referans ME/FAVORITES_ROUTE_ID
 * normalizasyonunun sadeleştirilmiş hali).
 */

import { makeAutoObservable } from "mobx";
import { makePersistent } from "@app/features/platform/utils/MobXPersistence";
import { Logger } from "@app/features/platform/utils/Logger";
import { ME_ROUTE } from "@app/features/navigation/utils/NavigationPaths";

const logger = new Logger("SelectedGuild");

/** Route guild segmentini normalize eder: DM/`@me`/boş → null, aksi halde id. */
function normalizeGuildFromRoute(guildId: string | null | undefined): string | null {
  if (!guildId || guildId === ME_ROUTE) {
    return null;
  }
  return guildId;
}

/** Seçili sunucu durumu (kaynak-referans `SelectedGuild` sınıf paritesi). */
class SelectedGuild {
  /** En son seçili sunucu (DM'e geçince de korunur; kalıcı). */
  lastSelectedGuildId: string | null = null;
  /** Aktif seçili sunucu (DM bağlamında null). */
  selectedGuildId: string | null = null;
  /** Aynı sunucuya tekrar navigasyonu bile tetikleyebilmek için artan sayaç. */
  selectionNonce = 0;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    // kaynak-referans `makePersistent(this, 'SelectedGuild', ['lastSelectedGuildId'])`
    // allowlist paritesi — yalnızca lastSelectedGuildId kalıcıdır. Anahtar eski
    // zustand `persist(name)` ile aynı; mevcut kullanıcı verisi devralınır.
    void makePersistent(this, "SelectedGuild", ["lastSelectedGuildId"]);
    // Bozuk/eski persist değerini güvenli varsayılana düşür (zustand `merge` paritesi).
    if (typeof this.lastSelectedGuildId !== "string") {
      this.lastSelectedGuildId = null;
    }
  }

  /** Route'tan gelen guild segmentiyle senkronize eder (kaynak-referans reaction paritesi). */
  syncFromRoute(routeGuildId: string | null | undefined): void {
    const normalized = normalizeGuildFromRoute(routeGuildId);
    if (normalized) {
      this.setGuild(normalized, { forceNonce: false });
    } else {
      this.clearSelection();
    }
  }

  /** Kullanıcı bir sunucu seçtiğinde (aynı sunucu olsa da nonce artar). */
  selectGuild(guildId: string): void {
    if (!guildId) {
      return;
    }
    this.setGuild(guildId, { forceNonce: true });
  }

  /** Mevcut sunucu seçimini yeniden zorlar (nonce bump). */
  syncCurrentGuild(): void {
    this.bumpNonce();
  }

  /** Sunucu seçimini temizler (DM'e geçiş). */
  deselectGuild(): void {
    this.clearSelection();
  }

  private setGuild(guildId: string, options?: { forceNonce?: boolean }): void {
    const hasChanged = guildId !== this.selectedGuildId;
    if (hasChanged) {
      this.lastSelectedGuildId = this.selectedGuildId;
      this.selectedGuildId = guildId;
      this.bumpNonce();
      logger.debug(`Selected guild: ${guildId}`);
      return;
    }
    if (options?.forceNonce) {
      this.bumpNonce();
    }
  }

  private clearSelection(): void {
    if (this.selectedGuildId == null) {
      return;
    }
    this.lastSelectedGuildId = this.selectedGuildId;
    this.selectedGuildId = null;
    this.bumpNonce();
  }

  private bumpNonce(): void {
    this.selectionNonce++;
  }
}

/** Seçili sunucu store singleton'ı (kaynak-referans default-export singleton paritesi). */
export const SelectedGuildStore = new SelectedGuild();
export default SelectedGuildStore;
